import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { format, subDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useLazyExecutarConciliacaoQuery, useLimparCacheMutation, TipoDivergenciaEnum } from '@/services/conciliacao-fiscal';
import type { ConciliacaoFilterDto } from '@/services/conciliacao-fiscal';
import { Loader2, Search, Trash2, Database, ChevronDown } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

interface FilterFormData {
  startDate: string;
  endDate: string;
  incluirPulos: boolean;
  incluirStatus: boolean;
  incluirGftLivro: boolean;
  tipo?: string;
}

interface FiltersSectionProps {
  onExecuteStart?: () => void;
  onExecute: (data: any, filters?: ConciliacaoFilterDto) => void;
  onExecuteError?: () => void;
  loading?: boolean;
  onClearResults?: () => void;
}

const getTipoLabel = (tipo: TipoDivergenciaEnum): string => {
  const labels: Record<TipoDivergenciaEnum, string> = {
    [TipoDivergenciaEnum.PULO_NUMERACAO]: 'Pulos VAR',
    [TipoDivergenciaEnum.EXCLUSIVO_DOC001]: 'Exclusivo VAR/Migrate',
    [TipoDivergenciaEnum.EXCLUSIVO_VALIDATOR]: 'Exclusivo Migrate',
    [TipoDivergenciaEnum.STATUS_DIVERGENTE]: 'Divergente VAR/Migrate',
    [TipoDivergenciaEnum.PULO_NUMERACAO_GFT]: 'Pulos GFT',
    [TipoDivergenciaEnum.EXCLUSIVO_GFT_LIVRO]: 'Exclusivo GFT/VAR',
    [TipoDivergenciaEnum.EXCLUSIVO_DOC001_GFT]: 'Exclusivo VAR/GFT',
    [TipoDivergenciaEnum.STATUS_DIVERGENTE_GFT]: 'Divergente GFT/VAR',
    [TipoDivergenciaEnum.STATUS_DIVERGENTE_GFT_VALIDATOR]: 'Divergente GFT/Migrate',
  };
  return labels[tipo] || tipo;
};

export default function FiltersSection({
  onExecuteStart,
  onExecute,
  onExecuteError,
  loading = false,
  onClearResults
}: FiltersSectionProps) {
  const [executarConciliacao, { isLoading }] = useLazyExecutarConciliacaoQuery();
  const [limparCache, { isLoading: isClearing }] = useLimparCacheMutation();
  const { toast } = useToast();

  const form = useForm<FilterFormData>({
    defaultValues: {
      startDate: format(subDays(new Date(), 7), 'yyyy-MM-dd'),
      endDate: format(new Date(), 'yyyy-MM-dd'),
      incluirPulos: true,
      incluirStatus: true,
      incluirGftLivro: false,
      tipo: 'TODOS',
    },
  });

  const [startDate, setStartDate] = useState<string>(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [tiposSelecionados, setTiposSelecionados] = useState<TipoDivergenciaEnum[]>([]);
  const [inutilizadoLogix, setInutilizadoLogix] = useState<string>('TODOS');
  const [statusDoc001, setStatusDoc001] = useState<string>('TODOS');
  const [statusValidator, setStatusValidator] = useState<string>('TODOS');
  const [statusGft, setStatusGft] = useState<string>('TODOS');

  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    form.setValue('startDate', value);
  };

  const handleEndDateChange = (value: string) => {
    setEndDate(value);
    form.setValue('endDate', value);
  };

  const handleSubmit = form.handleSubmit(async (data) => {
    try {
      // Iniciar loading e limpar estado anterior
      onExecuteStart?.();

      const params: ConciliacaoFilterDto = {
        startDate: data.startDate,
        endDate: data.endDate,
        incluirPulos: data.incluirPulos,
        incluirStatus: data.incluirStatus,
        incluirGftLivro: data.incluirGftLivro,
        page: 1,
        limit: 50,
      };

      // Adicionar filtro de tipos se houver selecionados
      if (tiposSelecionados && tiposSelecionados.length > 0) {
        params.tipos = tiposSelecionados;
      }

      // Adicionar filtro de inutilizadoLogix se não for "TODOS"
      if (inutilizadoLogix && inutilizadoLogix !== 'TODOS') {
        params.inutilizadoLogix = inutilizadoLogix === 'SIM';
      }

      // Adicionar filtros de status se não for "TODOS"
      if (statusDoc001 && statusDoc001 !== 'TODOS') {
        params.statusDoc001 = statusDoc001;
      }
      if (statusValidator && statusValidator !== 'TODOS') {
        params.statusValidator = statusValidator;
      }
      if (statusGft && statusGft !== 'TODOS') {
        params.statusGft = statusGft;
      }

      const result = await executarConciliacao(params).unwrap();
      onExecute(result, params);
    } catch (error) {
      console.error('Erro ao executar conciliação:', error);
      onExecuteError?.();
    }
  });


  const handleClearCache = async () => {
    try {
      await limparCache({}).unwrap();
      
      toast({
        title: '✅ Cache limpo com sucesso',
        description: 'Todos os dados em cache da conciliação fiscal foram removidos.',
        variant: 'default',
      });

      // Clear results listing after cache clear
      onClearResults?.();
    } catch (error) {
      console.error('Erro ao limpar cache:', error);
      
      toast({
        title: '❌ Erro ao limpar cache',
        description: 'Não foi possível limpar o cache. Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const handleClearFilters = () => {
    const defaultStart = format(subDays(new Date(), 7), 'yyyy-MM-dd');
    const defaultEnd = format(new Date(), 'yyyy-MM-dd');

    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    setTiposSelecionados([]);
    setInutilizadoLogix('TODOS');
    setStatusDoc001('TODOS');
    setStatusValidator('TODOS');
    setStatusGft('TODOS');
    form.reset({
      startDate: defaultStart,
      endDate: defaultEnd,
      incluirPulos: true,
      incluirStatus: true,
      incluirGftLivro: false,
      tipo: 'TODOS',
    });

    // Clear results listing
    onClearResults?.();
  };

  const isExecuting = isLoading || loading;
  const daysDifference = startDate && endDate
    ? Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  const isLargeRange = daysDifference > 30;

  return (
    <Card className="p-6 mb-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex flex-col md:flex-row md:items-end gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <Label className="block text-sm font-medium mb-1">
              Data Inicial
            </Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              disabled={isExecuting}
            />
          </div>

          <div className="flex-1 min-w-0">
            <Label className="block text-sm font-medium mb-1">
              Data Final
            </Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              min={startDate}
              disabled={isExecuting}
            />
          </div>

          <div className="flex-1 min-w-0">
            <Label className="block text-sm font-medium mb-1">
              Tipos de Divergência
            </Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  disabled={isExecuting}
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">
                    {tiposSelecionados.length === 0
                      ? 'Todos os tipos'
                      : tiposSelecionados.length === 1
                      ? getTipoLabel(tiposSelecionados[0])
                      : `${tiposSelecionados.length} tipos selecionados`}
                  </span>
                  <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[300px] p-0" align="start">
                <div className="max-h-[300px] overflow-y-auto p-2">
                  <div className="space-y-2">
                    {Object.values(TipoDivergenciaEnum).map((tipo) => (
                      <div key={tipo} className="flex items-center space-x-2">
                        <Checkbox
                          id={`tipo-${tipo}`}
                          checked={tiposSelecionados.includes(tipo)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setTiposSelecionados([...tiposSelecionados, tipo]);
                            } else {
                              setTiposSelecionados(tiposSelecionados.filter((t) => t !== tipo));
                            }
                          }}
                        />
                        <Label
                          htmlFor={`tipo-${tipo}`}
                          className="text-sm font-normal cursor-pointer flex-1"
                        >
                          {getTipoLabel(tipo)}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
                {tiposSelecionados.length > 0 && (
                  <div className="border-t p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setTiposSelecionados([])}
                      className="w-full"
                    >
                      Limpar seleção
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex-1 min-w-0">
            <Label className="block text-sm font-medium mb-1">
              Inutilizado Logix
            </Label>
            <Select
              value={inutilizadoLogix}
              onValueChange={setInutilizadoLogix}
              disabled={isExecuting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="SIM">Sim</SelectItem>
                <SelectItem value="NAO">Não</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-end gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <Label className="block text-sm font-medium mb-1">
              Status DOC001/VAR
            </Label>
            <Select
              value={statusDoc001}
              onValueChange={setStatusDoc001}
              disabled={isExecuting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="Autorizado">Autorizado</SelectItem>
                <SelectItem value="Cancelado">Cancelado</SelectItem>
                <SelectItem value="Pendente">Pendente</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 min-w-0">
            <Label className="block text-sm font-medium mb-1">
              Status Validator/Migrate
            </Label>
            <Select
              value={statusValidator}
              onValueChange={setStatusValidator}
              disabled={isExecuting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="APROVADO">Aprovado</SelectItem>
                <SelectItem value="CANCELADO">Cancelado</SelectItem>
                <SelectItem value="PENDENTE">Pendente</SelectItem>
                <SelectItem value="REJEITADO">Rejeitado</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex-1 min-w-0">
            <Label className="block text-sm font-medium mb-1">
              Status GFT
            </Label>
            <Select
              value={statusGft}
              onValueChange={setStatusGft}
              disabled={isExecuting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="TODOS">Todos</SelectItem>
                <SelectItem value="Autorizada">Autorizada</SelectItem>
                <SelectItem value="Cancelada">Cancelada</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 md:flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClearCache}
              disabled={isExecuting || isClearing}
              className="px-3 flex-shrink-0"
              size="sm"
              title="Limpar cache da conciliação"
            >
              {isClearing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Database className="h-4 w-4" />
              )}
              <span className="sr-only md:not-sr-only md:ml-2">Cache</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={handleClearFilters}
              disabled={isExecuting}
              className="px-3 flex-shrink-0"
              size="sm"
            >
              <Trash2 className="h-4 w-4" />
              <span className="sr-only md:not-sr-only md:ml-2">Limpar</span>
            </Button>

            <Button
              type="submit"
              disabled={isExecuting}
              className="px-4 md:px-6 flex-1 md:flex-initial"
              size="sm"
            >
              {isExecuting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="hidden sm:inline">Executando...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Executar Conciliação</span>
                  <span className="sm:hidden">Executar</span>
                </>
              )}
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="incluirPulos"
              checked={form.watch('incluirPulos')}
              onCheckedChange={(checked) => form.setValue('incluirPulos', !!checked)}
              disabled={isExecuting}
            />
            <Label htmlFor="incluirPulos" className="text-sm cursor-pointer">
              Incluir Pulos de Numeração
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="incluirStatus"
              checked={form.watch('incluirStatus')}
              onCheckedChange={(checked) => form.setValue('incluirStatus', !!checked)}
              disabled={isExecuting}
            />
            <Label htmlFor="incluirStatus" className="text-sm cursor-pointer">
              Incluir Status Divergentes
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="incluirGftLivro"
              checked={form.watch('incluirGftLivro')}
              onCheckedChange={(checked) => form.setValue('incluirGftLivro', !!checked)}
              disabled={isExecuting}
            />
            <Label htmlFor="incluirGftLivro" className="text-sm cursor-pointer">
              Incluir GFT Livro Fiscal
            </Label>
          </div>
        </div>

        {isLargeRange && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Período extenso detectado
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Período superior a 30 dias ({daysDifference} dias). O processamento será executado
                    de forma assíncrona e você receberá notificação quando estiver concluído.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </form>
    </Card>
  );
}